"use client";

import { useState } from "react";
import { PlatformError } from "@/lib/api/client";
import { messageFor, withdrawErrorMessages } from "@/lib/api/messages";
import { isKycRequiredError } from "@/lib/api/kyc";
import { useHandleKycRequired } from "@/lib/kyc/hooks";
import { KycVerifyIdentityBanner } from "@/components/kyc/KycVerifyIdentityBanner";
import { useWallets as useLinkedWallets } from "@/lib/api/profile";
import { useRequestWithdrawal, useWithdrawDetails, type RouteOption } from "@/lib/api/transfers";
import { formatAmount, shortenAddress, toBaseUnits } from "@/lib/format/amount";
import { formatMinimum, hasInvalidAmountCharacters, sanitizeAmountInput } from "@/lib/wallet/amount";
import { canWithdraw, validateWithdrawal, wageringHint, withdrawalAmount } from "@/lib/wallet/withdraw";
import { Icon } from "@/components/ui/Icon";
import { Button } from "@/components/ui/primitives";
import { SectionError } from "@/components/ui/states";
import { useEmailVerificationRedirect } from "./email-gate";
import { Field, Note } from "./fields";
import { GeoblockNotice } from "./GeoblockNotice";
import { Web3WalletCard, useWeb3Wallet } from "./Web3Wallet";

/* Withdrawing, against the route's own limits.

   `GET /withdraw/{routeId}` publishes the minimum, the rate and the pattern a
   destination address must match on this network — so the form can refuse a
   typo before it costs a round trip, and can say what the minimum actually is
   instead of letting the API answer 400 afterwards.

   Two tokens are in play and they are not interchangeable. The route's
   `toToken` is what arrives on the destination network — it names the network
   and the address the player must own. The **bankroll** token is what the
   withdrawal is taken from: the amount, the minimum and the balance are all in
   its base units, which is how the platform compares them and what the
   whitelabel sends (`parseUnits(amount, bankroll.token.decimals)`). */

export function WithdrawPanel({
  option,
  balance,
  locks,
}: {
  option: RouteOption;
  balance: string;
  locks: { trancheLockedAmount: string; amlLockedAmount: string };
}) {
  const details = useWithdrawDetails(option.route.id);

  if (details.isPending) {
    return <div className="h-[184px] w-full animate-pulse rounded-lg bg-elevation-2" />;
  }

  if (details.isError) {
    /* A 403 here is the region, not a fault — the whitelabel's Geoblock. */
    if (details.error instanceof PlatformError && details.error.status === 403) {
      return <GeoblockNotice />;
    }

    return (
      <SectionError
        title="Withdrawal limits didn’t load"
        body={messageFor(
          details.error instanceof PlatformError ? details.error : new PlatformError(0, "network", ""),
          "wallet",
        )}
      />
    );
  }

  return (
    <Form
      option={option}
      balance={balance}
      locks={locks}
      addressPattern={details.data.addressPattern}
      minimumAmount={details.data.minimumAmount}
    />
  );
}

function Form({
  option,
  balance,
  locks,
  addressPattern,
  minimumAmount,
}: {
  option: RouteOption;
  balance: string;
  locks: { trancheLockedAmount: string; amlLockedAmount: string };
  addressPattern: string;
  minimumAmount: string;
}) {
  /* Everything below the modal's sign-in gate, so the session is a given. */
  const linked = useLinkedWallets(true);
  const withdrawal = useRequestWithdrawal();
  const handleKycRequired = useHandleKycRequired();
  const redirectToEmailVerification = useEmailVerificationRedirect();

  const [address, setAddress] = useState("");
  const [amount, setAmount] = useState("");
  /* A required field says so only once the player has been in it, or tried to
     submit — an untouched form shows no red. */
  const [touched, setTouched] = useState({ address: false, amount: false });

  /* The whitelabel's "withdraw to connected wallet": the same hub-wide
     connection the deposit form uses, offered as the destination so the
     address never has to be typed — or mistyped. */
  const { connection } = useWeb3Wallet();
  const destination = option.token;
  const chainMatches = connection?.wallet.chain === destination.network.environment;

  /* The scale of every figure in this form. */
  const scale = option.bankroll.token;
  const errors = validateWithdrawal({ address, amount }, { addressPattern, minimumAmount, balance, token: scale });
  const ready = canWithdraw(errors);
  const hint = wageringHint({ ...locks, token: scale });
  const minimum = toBaseUnits(minimumAmount);

  const known = linked.data ?? [];

  const changeAmount = (value: string) => {
    setAmount(hasInvalidAmountCharacters(value) ? sanitizeAmountInput(value) : value);
    setTouched((current) => ({ ...current, amount: true }));
  };

  const changeAddress = (value: string) => {
    setAddress(value);
    setTouched((current) => ({ ...current, address: true }));
  };

  const submit = () => {
    setTouched({ address: true, amount: true });
    const parsed = withdrawalAmount(amount, scale);

    if (!ready || parsed === null) {
      return;
    }

    withdrawal.mutate(
      { routeId: option.route.id, address: address.trim(), amount: parsed },
      {
        onSuccess: () => {
          setAmount("");
          setAddress("");
          setTouched({ address: false, amount: false });
        },
        onError: (error) => {
          /* Refused until the player is verified: the prompt that clears it,
             not an error line (the whitelabel's onKycRequired). */
          if (isKycRequiredError(error)) {
            handleKycRequired();
            return;
          }

          /* No verified email: the reason is said, and the player is taken to
             where the email is added (the whitelabel's
             useEmailVerificationRedirect). */
          if (error instanceof PlatformError && error.message === "EMAIL_VERIFICATION_REQUIRED") {
            redirectToEmailVerification(withdrawErrorMessages(error, scale).join(" "));
          }
        },
      },
    );
  };

  const failure =
    withdrawal.isError && !isKycRequiredError(withdrawal.error)
      ? withdrawErrorMessages(
          withdrawal.error instanceof PlatformError ? withdrawal.error : new PlatformError(0, "network", ""),
          scale,
        )
      : null;

  return (
    <form
      className="flex w-full flex-col gap-[4px] rounded-lg border border-border p-[12px]"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <KycVerifyIdentityBanner />

      {hint && (
        <div className="mb-[8px] rounded-md bg-elevation-3 px-[12px] py-[8px] text-[12px] leading-[1.6] text-fg-subtle">
          {hint}
        </div>
      )}

      {["evm", "solana"].includes(destination.network.environment) && (
        <div className="mb-[4px]">
          <Web3WalletCard chain={destination.network.environment as "evm" | "solana"} />
          {connection && chainMatches && connection.address !== address && (
            <button
              type="button"
              onClick={() => changeAddress(connection.address)}
              className="mt-[4px] h-9 rounded-lg bg-elevation-3 px-4 text-[12px] font-bold transition-colors hover:bg-elevation-4"
            >
              Withdraw to {connection.wallet.name}
            </button>
          )}
        </div>
      )}

      <Field
        id="withdraw-address"
        label={`Destination address on ${destination.network.name}`}
        value={address}
        onChange={changeAddress}
        placeholder="Enter your wallet address"
        error={touched.address ? errors.address : null}
      />

      {/* An address the account has already proven it owns is one click rather
          than a paste nobody double-checks. */}
      {known.length > 0 && address.trim().length === 0 && (
        <div className="mt-[4px] flex flex-wrap gap-[8px]">
          {known.map((entry) => (
            <button
              key={entry.id}
              type="button"
              onClick={() => changeAddress(entry.address)}
              className="flex h-[26px] items-center rounded-rounded bg-elevation-3 px-[8px] text-[9px] font-bold leading-[1.5] text-fg-subtle transition-colors hover:text-fg"
            >
              {shortenAddress(entry.address)}
            </button>
          ))}
        </div>
      )}

      <div className="mt-[8px]" />

      <Field
        id="withdraw-amount"
        label={`Amount (${scale.symbol})`}
        value={amount}
        onChange={changeAmount}
        inputMode="decimal"
        placeholder="Enter amount"
        numeric
        error={touched.amount ? errors.amount : null}
        action={
          <button
            type="button"
            onClick={() => changeAmount(formatAmount(balance, scale, { grouped: false }))}
            className="flex h-[30px] items-center rounded-md bg-elevation-3 px-[12px] text-[9px] font-bold leading-[1.5] text-fg-subtle transition-colors hover:text-fg"
          >
            Max
          </button>
        }
      />

      <p className="mt-[4px] text-[9px] leading-[1.5] text-fg-subtle">
        Available {formatAmount(balance, scale)} {scale.symbol}
      </p>

      {/* The whitelabel's two lines under the form: whose address this must
          be, and the minimum — in the bankroll token, the scale it is in. */}
      <div className="mt-[4px] flex items-start gap-[8px] text-[9px] leading-[1.5] text-fg-subtle">
        <Icon name="info" size={14} className="mt-[1px] shrink-0 text-info-fg" />
        <div className="flex flex-col gap-[2px]">
          <p>
            Ensure this is your own {destination.network.name} wallet address ({destination.symbol} on{" "}
            {destination.network.name} network), or funds may be lost.
          </p>
          {minimum > 0n && (
            <p className="underline">
              Minimum withdrawal amount{" "}
              <strong className="text-fg">
                {formatMinimum(minimum, scale)} {scale.symbol}
              </strong>
              .
            </p>
          )}
        </div>
      </div>

      <Button
        type="submit"
        size="lg"
        className="mt-[8px] w-full"
        disabled={withdrawal.isPending || !ready}
      >
        {withdrawal.isPending ? "Requesting…" : "Withdraw"}
      </Button>

      {withdrawal.isSuccess && <Note tone="success">Withdrawal submitted</Note>}

      {failure && (
        <Note tone="error">
          {failure.map((line) => (
            <span key={line} className="block">
              {line}
            </span>
          ))}
        </Note>
      )}
    </form>
  );
}
